import React, { useState, useEffect } from 'react';
import {
    MDBCard, MDBCardBody, MDBCardHeader,
    MDBBtn, MDBTable, MDBTableBody, MDBTableHead,
    MDBCheckbox, MDBModal, MDBModalHeader,
    MDBModalBody, MDBModalFooter, MDBModalTitle,
    MDBSpinner, MDBAlert, MDBInput,
    MDBBadge, MDBRow, MDBCol
} from 'mdb-react-ui-kit';
import { getMemberOverdueCheckins, writeOffOverdueCheckins } from '../services/apiServices';

const ClearOverdue = ({ memberIds, memberName, gymId, onClose, onStartDateSelected }) => {

    const [checkins, setCheckins] = useState([]);

    const [selectedIds, setSelectedIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [writeOffReason, setWriteOffReason] = useState('');
    const [message, setMessage] = useState('');
    const [showConfirmDeleteAll, setShowConfirmDeleteAll] = useState(false);
    const [showConfirmClearAndRenew, setShowConfirmClearAndRenew] = useState(false);
    const [isCleared, setIsCleared] = useState(false);
    const [groupedByMember, setGroupedByMember] = useState({});
    const [memberStartDates, setMemberStartDates] = useState({});

    useEffect(() => {
        fetchOverdueCheckins();
    }, [memberIds, gymId]);

    const fetchOverdueCheckins = async () => {
        setLoading(true);
        try {
            const payload = {
                gymId,
                memberIds: Array.isArray(memberIds) ? memberIds : [memberIds]
            };

            const response = await getMemberOverdueCheckins(memberIds, payload);
            const data = await response.json();

            if (data.success) {
                setCheckins(data.data);

                // Group checkins by member for better organization
                const grouped = {};
                const startDates = {};

                data.data.forEach(checkin => {
                    const memberId = checkin.memberId;
                    if (!grouped[memberId]) {
                        grouped[memberId] = {
                            memberId: memberId,
                            memberName: checkin.memberName,
                            memberNumber: checkin.memberNumber,
                            checkins: []
                        };
                    }
                    grouped[memberId].checkins.push(checkin);
                });

                // Calculate oldest checkin date for EACH member
                Object.keys(grouped).forEach(memberId => {
                    const memberCheckins = grouped[memberId].checkins;
                    if (memberCheckins.length > 0) {
                        // Sort checkins by date (oldest first) for this member
                        const sortedCheckins = [...memberCheckins].sort((a, b) =>
                            new Date(a.intime) - new Date(b.intime)
                        );
                        const oldestCheckin = sortedCheckins[0];
                        startDates[memberId] = {
                            date: oldestCheckin.intime,
                            checkinId: oldestCheckin._id
                        };

                        // Auto-select the oldest checkin for each member
                        setSelectedIds(prev => [...prev, oldestCheckin._id]);
                    }
                });

                setGroupedByMember(grouped);
                setMemberStartDates(startDates);

                if (data.data.length === 0) {
                    setMessage('No overdue checkins found for selected members');
                    setTimeout(() => {
                        if (onClose) onClose();
                    }, 2000);
                } else {
                    setMessage(`Found ${data.data.length} overdue checkin(s) for ${memberIds.length} member(s)`);
                }
            } else {
                setMessage('Error loading overdue checkins');
            }
        } catch (error) {
            console.error(error);
            setMessage('Error loading overdue checkins');
        }
        setLoading(false);
    };

    const handleSelectCheckin = (checkinId, checkinDate, memberId) => {
        // Update start date for this specific member
        setMemberStartDates(prev => ({
            ...prev,
            [memberId]: { date: checkinDate, checkinId: checkinId }
        }));

        // Update selected IDs
        const newSelectedIds = selectedIds.filter(id => {
            const checkin = checkins.find(c => c._id === id);
            return !checkin || checkin.memberId !== memberId;
        });
        setSelectedIds([...newSelectedIds, checkinId]);
    };

    const handleClearAndRenew = () => {
        // Check if each member has a start date selected
        const missingDates = Object.keys(groupedByMember).filter(memberId =>
            !memberStartDates[memberId]
        );

        if (missingDates.length > 0) {
            setMessage('Please select a start date for each member');
            return;
        }

        setShowConfirmClearAndRenew(true);
    };

    const confirmClearAndRenew = async () => {
        setLoading(true);

        const totalOverdueCheckins = checkins?.length || 0;

        try {
            const overdueObject = {
                memberIds,
                gymId,
                memberStartDates: memberStartDates,
                totalOverdueCheckins: totalOverdueCheckins
            };


            const firstMemberId = memberIds?.[0];

            const selectedStartDate = memberStartDates?.[firstMemberId]?.date;

            // if (selectedStartDate && onStartDateSelected) {
            //     onStartDateSelected(selectedStartDate);
            // }
            if (overdueObject) {

                onStartDateSelected(overdueObject);

            }



            // Store ONLY the overdue data, NOT modified transaction dates
            // localStorage.setItem("overduecheckins", JSON.stringify(overdueObject));

            // Close the confirmation modal
            setShowConfirmClearAndRenew(false);

            // Close the entire popup immediately
            if (onClose) onClose();

        } catch (error) {
            console.error(error);
            setMessage("Something went wrong");
            setLoading(false);
        }
    };

    const handleDeleteAll = async () => {
        setLoading(true);
        try {
            const updates = {
                memberIds,
                gymId,

            };

            const response = await writeOffOverdueCheckins(updates);
            const result = await response.json();


            if (result.success) {
                // Close everything immediately without showing the checkins table again
                setMessage(`Successfully wrote off all overdue checkins`);
                setIsCleared(true);
                // setShowConfirmDeleteAll(false);

                setTimeout(() => {
                    setShowConfirmDeleteAll(false);
                    if (onClose) onClose();
                }, 1500);
            } else {
                setMessage('Error writing off checkins');
                setLoading(false);
            }
        } catch (error) {
            console.error('Error writing off checkins:', error);
            setMessage('Error writing off checkins');
        }
        setLoading(false);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatTime = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const handleClose = () => {
        setIsCleared(false);
        setMessage('');
        setSelectedIds([]);
        setMemberStartDates({});
        setShowConfirmDeleteAll(false);
        setShowConfirmClearAndRenew(false);
        if (onClose) onClose();
    };

    const cancelDeleteAll = () => {
        setShowConfirmDeleteAll(false);
        setWriteOffReason('');
    };

    // Calculate summary statistics
    const getSummaryStats = () => {
        const totalMembers = Object.keys(groupedByMember).length;
        const totalCheckins = checkins.length;
        const totalOverdueDays = checkins.reduce((sum, checkin) => {
            const checkinDate = new Date(checkin.intime);
            const expiryDate = new Date(checkin.overdueData?.planExpiryDate || checkin.intime);
            return sum + Math.ceil((checkinDate - expiryDate) / (1000 * 60 * 60 * 24));
        }, 0);

        return { totalMembers, totalCheckins, totalOverdueDays };
    };

    const stats = getSummaryStats();

    // Simple icon emojis
    const Icons = {
        user: '👤',
        calendar: '📅',
        clock: '⏰',
        warning: '⚠️',
        phone: '📞',
        check: '✅',
        close: '❌'
    };

    return (
        <>
            {message && (
                <MDBAlert color={message.includes('Successfully') ? 'success' : message.includes('Found') ? 'info' : 'danger'}
                    dismissible onClose={() => setMessage('')}>
                    {message}
                </MDBAlert>
            )}


            {loading && checkins.length === 0 ? (
                <div className="text-center py-4">
                    <MDBSpinner />
                    <p>Loading overdue checkins...</p>
                </div>
            ) : checkins.length === 0 && !isCleared ? (
                <div className="text-center py-4">
                    <p className="text-muted">No overdue checkins found.</p>
                </div>
            ) :
                showConfirmDeleteAll ? (
                    <div className="p-3" >
                        {isCleared && message.includes('Successfully') ? (
                            // SUCCESS VIEW - Show after successful deletion
                            <div className="text-center py-4">
                                <div className="alert alert-success">
                                    <h5>✅ Success!</h5>
                                    <p>All overdue check-ins have been successfully cleared for this member.</p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <h6 className="mb-3">Are you sure you want to Delete ALL overdue checkins?</h6>

                                {/* Show any messages (including errors) */}
                                {/* {message && (
                                    <div className={`alert ${message.includes('Error') ? 'alert-danger' : 'alert-info'}`}>
                                        {message}
                                    </div>
                                )} */}

                                <div className="alert alert-warning mt-3">
                                    <p>
                                        <strong>Note:</strong> This will permanently delete all overdue check-ins for the selected member.
                                    </p>
                                </div>
                                <div className="d-flex justify-content-end gap-2 mt-4">
                                    <MDBBtn color="secondary" size="md" onClick={cancelDeleteAll} disabled={loading || isCleared}>
                                        Cancel
                                    </MDBBtn>
                                    <MDBBtn color="danger" size="md" onClick={handleDeleteAll} disabled={loading || isCleared}>
                                        Confirm Delete All
                                    </MDBBtn>
                                </div>
                            </>
                        )}
                    </div >
                )
                    : showConfirmClearAndRenew ? (
                        <div className="p-3">
                            <h6 className="mb-3">Are you sure you want to proceed?</h6>

                            <div className="alert alert-info mb-3">
                                <strong> Plan Start Date:</strong>
                                {Object.entries(memberStartDates).map(([memberId, data]) => {
                                    const member = groupedByMember[memberId];
                                    return (
                                        <div key={memberId} className=" mt-2">
                                            <strong>{member.memberName}:</strong> {formatDate(data.date)}
                                        </div>
                                    );
                                })}
                            </div>
                            <ul className="mb-0">
                                <li>
                                    All <strong>overdue check-ins will be permanently cleared</strong> for the selected member.
                                </li>
                                <li>
                                    Member(s) will be renewed with their <strong>individually selected plan start date</strong>.
                                </li>
                                <li>
                                    Membership status will be updated to <strong>Active</strong>.
                                </li>
                                <li>
                                    Cleared overdue check-ins will be <strong>deducted from the new plan sessions</strong>.
                                </li>
                            </ul>

                            <div className="d-flex justify-content-end gap-2 mt-4">
                                <MDBBtn color="secondary" size="md" onClick={() => setShowConfirmClearAndRenew(false)} disabled={loading}>
                                    Cancel
                                </MDBBtn>
                                <MDBBtn color="primary" size="md" onClick={confirmClearAndRenew} disabled={loading}>
                                    Confirm Clear
                                </MDBBtn>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Cool Summary Section with Emoji Icons */}
                            {/* <h5 className="card-title mb-3">
                                    {Icons.warning} Overdue Checkins Summary
                                </h5>

                                <MDBRow className="mb-3">
                                    <MDBCol md="4">
                                        <div className="d-flex align-items-center p-3 bg-light rounded">
                                            <div className="bg-primary text-white rounded-circle p-3 me-3">
                                                <span style={{ fontSize: '24px' }}>{Icons.user}</span>
                                            </div>
                                            <div>
                                                <h6 className="mb-0">Total Members</h6>
                                                <h4 className="mb-0 text-primary">{stats.totalMembers}</h4>
                                            </div>
                                        </div>
                                    </MDBCol>
                                    <MDBCol md="4">
                                        <div className="d-flex align-items-center p-3 bg-light rounded">
                                            <div className="bg-success text-white rounded-circle p-3 me-3">
                                                <span style={{ fontSize: '24px' }}>{Icons.calendar}</span>
                                            </div>
                                            <div>
                                                <h6 className="mb-0">Total Checkins</h6>
                                                <h4 className="mb-0 text-success">{stats.totalCheckins}</h4>
                                            </div>
                                        </div>
                                    </MDBCol>
                                    <MDBCol md="4">
                                        <div className="d-flex align-items-center p-3 bg-light rounded">
                                            <div className="bg-warning text-white rounded-circle p-3 me-3">
                                                <span style={{ fontSize: '24px' }}>{Icons.clock}</span>
                                            </div>
                                            <div>
                                                <h6 className="mb-0">Total Overdue Days</h6>
                                                <h4 className="mb-0 text-warning">{stats.totalOverdueDays}</h4>
                                            </div>
                                        </div>
                                    </MDBCol>
                                </MDBRow> */}

                            {/* Individual Member Start Dates */}
                            <div className="mt-1 overflow-y-scroll " style={{ maxHeight: '480px', overflowX: 'hidden' }}>
                                <h6>Note: Select a check-in date below. The selected date will be used as the new plan start date.</h6>

                                <div className="row">
                                    {Object.entries(groupedByMember).map(([memberId, member]) => (
                                        <div key={memberId} className="col-md-6 mb-1">

                                            <div className="card shadow-0 border border-1 overflow-hidden">

                                                <div className="card-body p-3 pb-0">

                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <div>
                                                            <h6 className="mb-1">
                                                                {member.memberName}
                                                            </h6>
                                                            <p className="text-muted  mb-0">
                                                                {member.memberNumber && member.memberNumber !== 'N/A' &&
                                                                    ` ${member.memberNumber}`
                                                                }

                                                            </p>
                                                        </div>
                                                        <div className="text-end">
                                                            <span className="badge bg-warning ms-2">
                                                                {member.checkins.length} checkins
                                                            </span>


                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="px-3 py-2 mt-1" style={{ background: '#d2f3ff', fontSize: '13px' }}>
                                                    <label className='me-1'>Plan will start on :</label>
                                                    <strong className="text-primary fw-bold">
                                                        {memberStartDates[memberId] ?
                                                            formatDate(memberStartDates[memberId].date) :
                                                            'Not selected'
                                                        }
                                                    </strong>
                                                </div>

                                            </div>
                                        </div>
                                    ))}
                                </div>


                                {/* Checkins Table */}
                                <div className="datatable cursorPointer mmManager_Table mt-3 overflow-hidden " data-mdb-fixed-header="true">
                                    <div className="mmtablediv overdue-table  overflow-hidden">
                                        <MDBTable responsive hover small scroll className="table-striped">
                                            <MDBTableHead className="table-dark">
                                                <tr>
                                                    <th style={{ width: "60px" }}>Select</th>
                                                    {/* <th>Member</th> */}
                                                    <th>Check-in Date</th>
                                                    <th>Plan Expired On</th>
                                                    <th>Days Overdue</th>
                                                </tr>
                                            </MDBTableHead>

                                            <MDBTableBody>
                                                {Object.entries(groupedByMember).map(([memberId, memberData]) => (
                                                    <React.Fragment key={memberId}>
                                                        <tr className="overdue-table-header" >
                                                            <td colSpan="6">
                                                                <div className='d-flex justify-content-between'>
                                                                    <h6 className='mb-0 text-primary'>  {memberData.memberName} </h6>
                                                                    <span className="badge bg-warning text-dark ms-2">
                                                                        {memberData.checkins.length} checkins
                                                                    </span>
                                                                </div>


                                                            </td>
                                                        </tr>
                                                        {memberData.checkins.map((checkin) => {
                                                            const checkinDate = new Date(checkin.intime);
                                                            const expiryDate = new Date(
                                                                checkin.overdueData?.planExpiryDate || checkin.intime
                                                            );
                                                            const daysOverdue = Math.ceil(
                                                                (checkinDate - expiryDate) / (1000 * 60 * 60 * 24)
                                                            );

                                                            const isSelected = memberStartDates[memberId]?.checkinId === checkin._id;
                                                            const isOverdueSevere = daysOverdue > 30;
                                                            const isOverdueHigh = daysOverdue > 7 && daysOverdue <= 30;

                                                            return (
                                                                <tr
                                                                    key={checkin._id}
                                                                    className={isSelected ? 'table-success' : ''}
                                                                    style={isSelected ? {
                                                                        backgroundColor: '#d1e7dd',
                                                                        fontWeight: 'bold'
                                                                    } : {}}
                                                                >
                                                                    <td className="text-center">
                                                                        <MDBCheckbox
                                                                            checked={isSelected}
                                                                            onChange={() => handleSelectCheckin(checkin._id, checkin.intime, memberId)}
                                                                            disabled={loading}
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <div className="d-flex align-items-center">
                                                                            {formatDate(checkin.intime)}
                                                                            {/* {isSelected && (
                                                                        <span className="badge bg-success ms-2">
                                                                            {Icons.check} Start Date
                                                                        </span>
                                                                    )} */}
                                                                        </div>
                                                                    </td>
                                                                    {/* <td>{formatTime(checkin.intime)}</td> */}
                                                                    <td>{formatDate(expiryDate)}</td>
                                                                    <td>
                                                                        <span className={`badge ${isOverdueSevere ? "bg-danger" : isOverdueHigh ? "bg-warning" : "bg-info"}`}>
                                                                            {daysOverdue} days
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </React.Fragment>
                                                ))}
                                            </MDBTableBody>
                                        </MDBTable>
                                    </div>
                                </div>

                                {/* Action Buttons */}

                            </div>
                            <div className="d-flex justify-content-between align-items-center mt-4">
                                <div>
                                    <MDBBtn color="primary" size="md" onClick={handleClearAndRenew} disabled={loading}>
                                        <strong>Start Plans </strong>
                                    </MDBBtn>
                                    <MDBBtn color="secondary" size="md" onClick={handleClose} className="ms-2" disabled={loading}>
                                        Cancel
                                    </MDBBtn>
                                </div>
                                <div className="text-end">
                                    <MDBBtn color="danger" size="md" onClick={() => setShowConfirmDeleteAll(true)} disabled={checkins.length === 0 || loading}>
                                        Delete All Overdues
                                    </MDBBtn>
                                </div>
                            </div>
                        </>
                    )}
        </>
    );
};

export default ClearOverdue;